"use client"

import { ChevronLeft, ChevronRight, Menu, Search, X } from "lucide-react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import * as React from "react"

import AppTitle from "@/components/layout/AppTitle"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/input"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

// --------------------
// Types
// --------------------
interface NavigationItemGroup {
  label: string
  items: NavigationItem[]
}

interface NavigationItem {
  name: string
  href?: string
  icon?: React.ComponentType<{ className?: string }>
  badge?: string
  description?: string
  tooltip?: string
  children?: NavigationItem[]
  /** Groups for organizing children into multiple columns */
  groups?: NavigationItemGroup[]
}

interface NavigationGroup {
  label: string
  items: NavigationItem[]
}

interface MainNavProps {
  className?: string
}

// Single navigation configuration: menu items from HeaderNav
const getGroupsItems = (): NavigationGroup => {
  const baseItems: NavigationItem[] = [
    {
      name: "Resources",
      href: "/resources",
      description: "Access UIC resources and tools",
      groups: [
        {
          label: "",
          items: [
            { name: "Safer-LC", href: "/safer-lc" },
            { name: "Restrail", href: "/restrail" },
            { name: "New Technologies", href: "/newtech" },
            { name: "RailHOF", href: "https://railhof.org/" },
            {
              name: "UIC Safety Public Report",
              href: "https://app.powerbi.com/view?r=eyJrIjoiMWU4ZGE4Y2QtNWMyYS00MGZkLWE5YTctODdhNWNjZDg2ZTU4IiwidCI6IjQ3MWFlM2Q1LWVmZTYtNGMzNS05NGUxLTYxYWQ1YjllNDNkNSIsImMiOjh9",
            },
            {
              name: "UIC Safety Report Focus 2024",
              href: "https://app.powerbi.com/view?r=eyJrIjoiNjlmZjZiYjUtMmE4NC00NjFkLTkxYjQtYTkxNzI3MWIxNmU2IiwidCI6IjQ3MWFlM2Q1LWVmZTYtNGMzNS05NGUxLTYxYWQ1YjllNDNkNSIsImMiOjh9",
            },
          ],
        },
      ],
    },

    {
      name: "Events",
      href: "#",
      description: "",
      groups: [
        {
          label: "",
          items: [
            { name: "ILCAD", href: "https://ilcad.org/" },
            { name: "TRESPAD", href: "https://trespad.org/" },
            { name: "Webinars", href: "/events/webinars" },
            { name: "Calendar", href: "/events/calendar" },
          ],
        },
      ],
    },
    {
      name: "Services",
      href: "/services",
      description: "UIC services and training programs",
      groups: [
        {
          label: "",
          items: [
            { name: "Peer Review", href: "/pages/safety-culture-peer-review" },
            {
              name: "Trainings - Safety Culture",
              href: "/services/trainings-safety-culture",
            },
            { name: "Trainings - HOF", href: "/services/trainings-hof" },
          ],
        },
      ],
    },
    {
      name: "Projects",
      href: "/projects",
      description: "Browse UIC projects",
    },
    // {
    //   name: "Calendar",
    //   href: "/events/calendar",
    //   description: "View UIC events calendar",
    // },
    // {
    //   name: "Publications",
    //   href: "https://shop.uic.org/en/search?s=Safety+human+factors&order=product.date_add.desc",
    //   description: "Browse UIC publications and documents",
    //   tooltip: "Opens in a new window - Shop Railway Publications",
    // },
    {
      name: "Publications",
      href: "/publications",
      description: "Browse UIC publications",
    },
    {
      name: "Our Team",
      href: "/our-team",
      description: "Meet our team members",
    },
  ]

  return {
    label: "Main",
    items: baseItems,
  }
}

// Type-guard helpers
const hasHref = (
  item: NavigationItem
): item is NavigationItem & { href: string } => Boolean(item.href)

export function MainNav({ className }: MainNavProps) {
  const pathname = usePathname()
  const router = useRouter()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [activeItem, setActiveItem] = React.useState<HTMLElement | null>(null)
  const [searchOpen, setSearchOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")
  const menuListRef = React.useRef<HTMLUListElement>(null)
  const [openGroupsSheets, setOpenGroupsSheets] = React.useState<
    Record<string, boolean>
  >({})
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  // Get navigation items (filtered by environment)
  const groupsItems = React.useMemo(() => getGroupsItems(), [])

  // Gérer le survol des éléments du menu
  const handleMouseEnter = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.currentTarget
      if (!menuListRef.current) return

      const rect = target.getBoundingClientRect()
      const listRect = menuListRef.current.getBoundingClientRect()

      menuListRef.current.style.setProperty(
        "--underline-width",
        `${rect.width}px`
      )
      menuListRef.current.style.setProperty(
        "--underline-x",
        `${rect.left - listRect.left}px`
      )
    },
    []
  )

  // Réinitialiser la position de la bordure quand la souris quitte le menu
  const handleMouseLeave = React.useCallback(() => {
    if (!menuListRef.current) return

    // Si on a un élément actif, on garde sa bordure
    if (activeItem) {
      const rect = activeItem.getBoundingClientRect()
      const listRect = menuListRef.current.getBoundingClientRect()
      menuListRef.current.style.setProperty(
        "--underline-width",
        `${rect.width}px`
      )
      menuListRef.current.style.setProperty(
        "--underline-x",
        `${rect.left - listRect.left}px`
      )
    } else {
      // Sinon on réinitialise la bordure
      menuListRef.current.style.setProperty("--underline-width", "0px")
      menuListRef.current.style.setProperty("--underline-x", "0px")
    }
  }, [activeItem])

  // Mettre à jour l'élément actif quand le pathname change
  React.useEffect(() => {
    if (!menuListRef.current) return

    const activeLink = menuListRef.current.querySelector(
      '[aria-current="page"]'
    ) as HTMLElement
    if (activeLink) {
      setActiveItem(activeLink)
      const rect = activeLink.getBoundingClientRect()
      const listRect = menuListRef.current.getBoundingClientRect()

      menuListRef.current.style.setProperty(
        "--underline-width",
        `${rect.width}px`
      )
      menuListRef.current.style.setProperty(
        "--underline-x",
        `${rect.left - listRect.left}px`
      )
    } else {
      setActiveItem(null)
      menuListRef.current.style.setProperty("--underline-width", "0px")
      menuListRef.current.style.setProperty("--underline-x", "0px")
    }
  }, [pathname])

  const handleLinkClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setMobileMenuOpen(false)
      setOpenGroupsSheets({})

      // Mettre à jour la position de la bordure pour le lien cliqué
      const target = event.currentTarget
      if (!menuListRef.current) return

      const rect = target.getBoundingClientRect()
      const listRect = menuListRef.current.getBoundingClientRect()

      menuListRef.current.style.setProperty(
        "--underline-width",
        `${rect.width}px`
      )
      menuListRef.current.style.setProperty(
        "--underline-x",
        `${rect.left - listRect.left}px`
      )
      setActiveItem(target)
    },
    []
  )

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchOpen(false)
      setSearchQuery("")
    }
  }

  // Focus search input when search is opened
  React.useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [searchOpen])

  return (
    <div className={cn("", className)}>
      <div className="flex items-center">
        <AppTitle className="mr-8" showLabel={false} />
        <NavigationMenu
          className="hidden h-16 md:h-[90px] md:flex"
          viewport={false}
        >
          <NavigationMenuList ref={menuListRef} onMouseLeave={handleMouseLeave}>
            {groupsItems.items.map((item) => {
              const hasSubmenu =
                (item.groups && item.groups.length > 0) ||
                (item.children && item.children.length > 0)

              return item.href && !hasSubmenu ? (
                <NavigationMenuItem
                  key={item.href}
                  className="h-16 md:h-[90px]"
                >
                  {item.tooltip ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <NavigationMenuLink
                          active={pathname === item.href}
                          asChild
                        >
                          <Link
                            href={item.href}
                            onMouseEnter={handleMouseEnter}
                            onClick={handleLinkClick}
                            target={
                              item.href.startsWith("http")
                                ? "_blank"
                                : undefined
                            }
                            rel={
                              item.href.startsWith("http")
                                ? "noopener noreferrer"
                                : undefined
                            }
                            className={cn(
                              "h-full",
                              pathname === item.href && "text-accent-foreground"
                            )}
                            aria-current={
                              pathname === item.href ? "page" : undefined
                            }
                          >
                            {item.name}
                          </Link>
                        </NavigationMenuLink>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>{item.tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <NavigationMenuLink active={pathname === item.href} asChild>
                      <Link
                        href={item.href}
                        onMouseEnter={handleMouseEnter}
                        onClick={handleLinkClick}
                        target={
                          item.href.startsWith("http") ? "_blank" : undefined
                        }
                        rel={
                          item.href.startsWith("http")
                            ? "noopener noreferrer"
                            : undefined
                        }
                        className={cn(
                          "h-full",
                          pathname === item.href && "text-accent-foreground"
                        )}
                        aria-current={
                          pathname === item.href ? "page" : undefined
                        }
                      >
                        {item.name}
                      </Link>
                    </NavigationMenuLink>
                  )}
                </NavigationMenuItem>
              ) : item.href && hasSubmenu ? (
                <NavigationMenuItem
                  key={item.href}
                  className="relative h-14 md:h-[78px]"
                >
                  <NavigationMenuTrigger
                    onMouseEnter={handleMouseEnter}
                    className={cn(
                      "h-full",
                      pathname === item.href && "text-accent-foreground"
                    )}
                    aria-current={pathname === item.href ? "page" : undefined}
                  >
                    {item.name}
                  </NavigationMenuTrigger>

                  <NavigationMenuContent className="top-[calc(100%+0.25rem)]! left-0!">
                    {item.groups && item.groups.length > 0 ? (
                      <div
                        className={`grid gap-4 rounded-none border-none p-6 ${
                          item.groups.length === 1
                            ? "w-[280px]"
                            : item.groups.length === 2
                              ? "w-[560px] md:grid-cols-2"
                              : "w-[840px] md:grid-cols-3"
                        }`}
                      >
                        {item.groups.map((group, groupIndex) => (
                          <div key={groupIndex} className="space-y-3">
                            {group.label && (
                              <h4 className="text-base font-semibold">
                                {group.label}
                              </h4>
                            )}
                            <ul className="grid gap-2">
                              {group.items.filter(hasHref).map((child) => {
                                const isExternal = child.href.startsWith("http")
                                return (
                                  <li key={child.href}>
                                    <NavigationMenuLink asChild>
                                      <Link
                                        href={child.href}
                                        onClick={handleLinkClick}
                                        target={
                                          isExternal ? "_blank" : undefined
                                        }
                                        rel={
                                          isExternal
                                            ? "noopener noreferrer"
                                            : undefined
                                        }
                                        className="grid min-h-[44px] w-full items-center justify-start gap-1 rounded-md px-3 py-3 text-base font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                      >
                                        <div className="text-base leading-normal font-medium">
                                          {child.name}
                                        </div>
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>
                                )
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ) : item.children && item.children.length > 0 ? (
                      <div className="w-[280px] rounded-none border-none p-6">
                        <div className="space-y-3">
                          <h4 className="text-base font-semibold">
                            {item.name}
                          </h4>
                          <ul className="grid gap-2">
                            {item.children.filter(hasHref).map((child) => {
                              const isExternal = child.href.startsWith("http")
                              return (
                                <li key={child.href}>
                                  <NavigationMenuLink asChild>
                                    <Link
                                      href={child.href}
                                      onClick={handleLinkClick}
                                      target={isExternal ? "_blank" : undefined}
                                      rel={
                                        isExternal
                                          ? "noopener noreferrer"
                                          : undefined
                                      }
                                      className="grid min-h-[44px] w-full items-center justify-start gap-1 rounded-md px-3 py-3 text-base font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    >
                                      <div className="text-base leading-normal font-medium">
                                        {child.name}
                                      </div>
                                    </Link>
                                  </NavigationMenuLink>
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      </div>
                    ) : null}
                  </NavigationMenuContent>
                </NavigationMenuItem>
              ) : null
            })}
          </NavigationMenuList>
        </NavigationMenu>

        {/* Search Button and Input */}
        <Sheet open={searchOpen} onOpenChange={setSearchOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="ml-2">
              <Search className="size-5" />
              <span className="sr-only">Search</span>
            </Button>
          </SheetTrigger>
          <SheetContent
            side="top"
            className="top-0 w-full p-2 md:p-4 [&>button]:hidden [&>div[data-slot=sheet-overlay]]:hidden"
            onInteractOutside={() => setSearchOpen(false)}
            onEscapeKeyDown={() => setSearchOpen(false)}
          >
            <SheetHeader>
              <div className="flex items-center justify-end mb-4">
                <SheetTitle className="sr-only">Search</SheetTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchOpen(false)}
                  aria-label="Close search"
                >
                  <X className="size-5" />
                </Button>
              </div>
            </SheetHeader>
            <form
              onSubmit={handleSearch}
              className="mx-auto flex w-full max-w-2xl gap-2"
            >
              <Input
                ref={searchInputRef}
                type="search"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" variant="ghost" size="icon">
                <Search className="size-5" />
                <span className="sr-only">Search</span>
              </Button>
            </form>
          </SheetContent>
        </Sheet>

        {/* Mobile Navigation */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-label={
                mobileMenuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="size-6" />
              ) : (
                <Menu className="size-6" />
              )}
              <span className="sr-only">
                {mobileMenuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"}
              </span>
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="top-0 w-full overflow-y-auto p-2 md:p-4 [&>button]:hidden [&>div[data-slot=sheet-overlay]]:hidden"
            onInteractOutside={() => setMobileMenuOpen(false)}
            onEscapeKeyDown={() => setMobileMenuOpen(false)}
          >
            <div className="flex items-center justify-between mb-4 pb-4 border-b">
              <SheetTitle>Navigation</SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close navigation menu"
              >
                <X className="size-5" />
              </Button>
            </div>
            <nav className="grid gap-6 p-2" aria-label="Mobile navigation">
              {/* Map all top-level items */}
              {groupsItems.items.map((item) => {
                // Render items with href but no children and no groups (simple links)
                if (
                  item.href &&
                  (!item.children || item.children.length === 0) &&
                  (!item.groups || item.groups.length === 0)
                ) {
                  const isExternal = item.href.startsWith("http")
                  const linkContent = (
                    <Link
                      href={item.href}
                      onClick={handleLinkClick}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noopener noreferrer" : undefined}
                      className="hover:text-accent-foreground flex w-full items-center py-1 text-lg font-semibold transition-colors"
                      aria-current={pathname === item.href ? "page" : undefined}
                    >
                      {item.name}
                    </Link>
                  )

                  return (
                    <div key={item.href} className="flex flex-col">
                      {item.tooltip ? (
                        <Tooltip>
                          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                          <TooltipContent side="right">
                            <p>{item.tooltip}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        linkContent
                      )}
                      {item.description && (
                        <p className="text-muted-foreground text-sm">
                          {item.description}
                        </p>
                      )}
                    </div>
                  )
                }

                // Render items with href and groups (items with grouped submenus)
                // Use Sheet pattern like _other_project for mobile navigation
                if (item.href && item.groups && item.groups.length > 0) {
                  const itemHref = item.href
                  const sheetKey = `groups-${itemHref}`
                  const isSheetOpen = openGroupsSheets[sheetKey] || false

                  return (
                    <div key={item.href} className="flex flex-col">
                      <Sheet
                        open={isSheetOpen}
                        onOpenChange={(open) => {
                          setOpenGroupsSheets((prev) => ({
                            ...prev,
                            [sheetKey]: open,
                          }))
                        }}
                      >
                        <SheetTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="hover:text-accent-foreground flex w-full items-center py-1 text-lg font-semibold transition-colors"
                            aria-label={`Toggle ${item.name} menu`}
                            aria-expanded={isSheetOpen}
                          >
                            {item.name}
                            <ChevronRight className="ml-auto size-5 transition-transform duration-200" />
                          </Button>
                        </SheetTrigger>
                        <SheetContent
                          side="left"
                          className="top-0 w-full gap-0 overflow-y-auto [&>button]:hidden [&>div[data-slot=sheet-overlay]]:hidden"
                          onInteractOutside={() => {
                            setOpenGroupsSheets((prev) => ({
                              ...prev,
                              [sheetKey]: false,
                            }))
                          }}
                          onEscapeKeyDown={() => {
                            setOpenGroupsSheets((prev) => ({
                              ...prev,
                              [sheetKey]: false,
                            }))
                          }}
                        >
                          <SheetHeader className="border-accent border-y p-1">
                            <div className="flex items-center justify-between">
                              <SheetTitle className="flex items-center gap-2">
                                <SheetClose asChild>
                                  <Button
                                    variant="ghost"
                                    size="lg"
                                    className="text-lg"
                                  >
                                    <ChevronLeft />
                                    <span className="sr-only">Back</span>
                                    {item.name}
                                  </Button>
                                </SheetClose>
                              </SheetTitle>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setOpenGroupsSheets((prev) => ({
                                    ...prev,
                                    [sheetKey]: false,
                                  }))
                                }}
                                aria-label="Close menu"
                              >
                                <X className="size-5" />
                              </Button>
                            </div>
                          </SheetHeader>
                          <div className="grid grid-cols-1 gap-6 p-6">
                            {item.groups.map((group, groupIndex) => (
                              <div key={groupIndex}>
                                {group.label && (
                                  <h4 className="text-muted-foreground mb-3 text-base font-semibold">
                                    {group.label}
                                  </h4>
                                )}
                                <div className="grid gap-2">
                                  {group.items.filter(hasHref).map((child) => {
                                    const isExternal =
                                      child.href.startsWith("http")
                                    return (
                                      <Link
                                        key={child.href}
                                        href={child.href}
                                        onClick={handleLinkClick}
                                        target={
                                          isExternal ? "_blank" : undefined
                                        }
                                        rel={
                                          isExternal
                                            ? "noopener noreferrer"
                                            : undefined
                                        }
                                        className="hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex min-h-[44px] w-full items-center rounded-md px-4 py-3 text-base font-medium transition-colors"
                                      >
                                        {child.name}
                                      </Link>
                                    )
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </SheetContent>
                      </Sheet>
                      {item.description && (
                        <p className="text-muted-foreground text-sm">
                          {item.description}
                        </p>
                      )}
                    </div>
                  )
                }

                // Render items with href and children (items with simple submenus)
                // Use Sheet pattern like _other_project for mobile navigation
                if (
                  item.href &&
                  item.children &&
                  item.children.length > 0 &&
                  (!item.groups || item.groups.length === 0)
                ) {
                  const itemHref = item.href
                  const sheetKey = `children-${itemHref}`
                  const isSheetOpen = openGroupsSheets[sheetKey] || false

                  return (
                    <div key={item.href} className="flex flex-col">
                      <Sheet
                        open={isSheetOpen}
                        onOpenChange={(open) => {
                          setOpenGroupsSheets((prev) => ({
                            ...prev,
                            [sheetKey]: open,
                          }))
                        }}
                      >
                        <SheetTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="hover:text-accent-foreground flex w-full items-center py-1 text-lg font-semibold transition-colors"
                            aria-label={`Toggle ${item.name} menu`}
                            aria-expanded={isSheetOpen}
                          >
                            {item.name}
                            <ChevronRight className="ml-auto size-5 transition-transform duration-200" />
                          </Button>
                        </SheetTrigger>
                        <SheetContent
                          side="left"
                          className="top-0 w-full gap-0 overflow-y-auto [&>button]:hidden [&>div[data-slot=sheet-overlay]]:hidden"
                          onInteractOutside={() => {
                            setOpenGroupsSheets((prev) => ({
                              ...prev,
                              [sheetKey]: false,
                            }))
                          }}
                          onEscapeKeyDown={() => {
                            setOpenGroupsSheets((prev) => ({
                              ...prev,
                              [sheetKey]: false,
                            }))
                          }}
                        >
                          <SheetHeader className="border-accent border-y p-1">
                            <div className="flex items-center justify-between">
                              <SheetTitle className="flex items-center gap-2">
                                <SheetClose asChild>
                                  <Button
                                    variant="ghost"
                                    size="lg"
                                    className="text-lg"
                                  >
                                    <ChevronLeft />
                                    <span className="sr-only">Back</span>
                                    {item.name}
                                  </Button>
                                </SheetClose>
                              </SheetTitle>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setOpenGroupsSheets((prev) => ({
                                    ...prev,
                                    [sheetKey]: false,
                                  }))
                                }}
                                aria-label="Close menu"
                              >
                                <X className="size-5" />
                              </Button>
                            </div>
                          </SheetHeader>
                          <div className="grid grid-cols-1 gap-6 p-6">
                            <div>
                              <h4 className="text-muted-foreground mb-3 text-base font-semibold">
                                {item.name}
                              </h4>
                              <div className="grid gap-2">
                                {item.children.filter(hasHref).map((child) => {
                                  const isExternal =
                                    child.href.startsWith("http")
                                  return (
                                    <Link
                                      key={child.href}
                                      href={child.href}
                                      onClick={handleLinkClick}
                                      target={isExternal ? "_blank" : undefined}
                                      rel={
                                        isExternal
                                          ? "noopener noreferrer"
                                          : undefined
                                      }
                                      className="hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex min-h-[44px] w-full items-center rounded-md px-4 py-3 text-base font-medium transition-colors"
                                    >
                                      {child.name}
                                    </Link>
                                  )
                                })}
                              </div>
                            </div>
                          </div>
                        </SheetContent>
                      </Sheet>
                      {item.description && (
                        <p className="text-muted-foreground text-sm">
                          {item.description}
                        </p>
                      )}
                    </div>
                  )
                }

                // Return null for any other items
                return null
              })}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  )
}
